import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { brokeredPreviewStorage } from './previewAuthStorage';
import { getStoredAdminOrganizationId } from '@/lib/adminOrganizationContext';
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from './config';

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

const configuredUrl = new URL(SUPABASE_URL);
const configuredPath = configuredUrl.pathname.replace(/\/+$/, '');
const organizationServicePaths = ['rest', 'functions', 'storage'].map(service =>
  configuredPath + '/' + service + '/v1',
);

const tenantAwareFetch: typeof fetch = (input, init) => {
  const url = typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.toString()
      : input.url;
  let target: URL;
  try { target = new URL(url); } catch { return fetch(input, init); }
  // Headers belong only to the configured project and exact API path segments.
  // Prefix/lookalike hosts and route-looking query strings must never receive it.
  if (target.origin !== configuredUrl.origin || target.username || target.password ||
      !organizationServicePaths.some(path => target.pathname === path || target.pathname.startsWith(path + '/'))) {
    return fetch(input, init);
  }
  const orgId = getStoredAdminOrganizationId();
  if (!orgId) return fetch(input, init);
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  headers.set('X-Akiva-Organization-Id', orgId);
  return fetch(input, { ...init, headers });
};

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: brokeredPreviewStorage(),
    persistSession: true,
    autoRefreshToken: true,
  },
  global: { fetch: tenantAwareFetch },
});
